import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useInboxUnreadCount } from './useInboxUnreadCount';
import { setNextRefreshAtAction } from 'src/store/slices/inboxUnreadCountSlice';

const POLL_INTERVAL_MS = 90_000;

export const useInboxUnreadCountPolling = (
  userId: string | undefined
): void => {
  const dispatch = useDispatch();

  const { refreshUnreadCount, nextRefreshAt } = useInboxUnreadCount();

  const armNextPollCycle = (): void => {
    dispatch(setNextRefreshAtAction(Date.now() + POLL_INTERVAL_MS));
  };

  const seedUnreadCount = (): void => {
    void refreshUnreadCount();
    armNextPollCycle();
  };

  const runScheduledRefresh = async (): Promise<void> => {
    if (!document.hidden) {
      await refreshUnreadCount();
    }

    armNextPollCycle();
  };

  const refreshOnReturnToTab = async (): Promise<void> => {
    if (document.hidden) {
      return;
    }

    await refreshUnreadCount();
    armNextPollCycle();
  };

  useEffect(() => {
    if (!userId) {
      return;
    }

    if (nextRefreshAt === null) {
      seedUnreadCount();
      return;
    }

    const delayMs: number = Math.max(0, nextRefreshAt - Date.now());

    const timeoutId: NodeJS.Timeout = setTimeout(
      () => void runScheduledRefresh(),
      delayMs
    );

    document.addEventListener('visibilitychange', refreshOnReturnToTab);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', refreshOnReturnToTab);
    };
  }, [userId, nextRefreshAt]);
};

